/**
 * TOOL 一覧ページ（実験的機能のハブ）。
 *
 * グローバルメニューの TOOL から遷移する。ツール追加時はここにカードを
 * 1 枚足し、routes.tsx に `/tool/<slug>` のルートを追加する。
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import phraseBloomLogo from "../../assets/images/card_tool_phrase_bloom.webp";
import reLocateLogo from "../../assets/images/card_tool_re_locate.webp";
import rendaKLogo from "../../assets/images/card_tool_renda_k.webp";
import stepEstimateLogo from "../../assets/images/card_tool_step_estimate.webp";

/** ロゴ画像の地色（全ツール共通）。letterbox 部分とシームレスに繋げる */
const TOOL_LOGO_BG = "#dcdedd";

/** カード外枠色。アプリ標準の --soremo-border (#f0f0f0) では白地に埋もれる
 *  ため、ロゴ地色と同系のやや濃いグレーで輪郭を立たせる */
const TOOL_CARD_BORDER = "#a8b2bc";

const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

/** ロゴ面 + 白地の情報部で構成するツールカード */
const ToolCard = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid ${TOOL_CARD_BORDER};
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 2px 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
  }
`;

/** ロゴを大きく見せる面。全ツール共通でロゴ画像の地色を敷く */
const ToolCardVisual = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${TOOL_LOGO_BG};
  border-bottom: 1px solid var(--soremo-border);
`;

const ToolCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/** タグ・説明テキストを載せる白地の情報部 */
const ToolCardBody = styled.div`
  padding: 14px 20px 18px;
  background: #fff;
`;

const ToolTag = styled.span.attrs({ className: "bodytext-11" })`
  align-self: flex-start;
  color: var(--soremo-gray);
  border: 1px solid var(--soremo-border);
  border-radius: 10px;
  padding: 1px 10px;
  background: var(--soremo-background);
`;

const Tools: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="u-col-center u-gap24" style={{ paddingBlock: 32 }}>
      <div className="u-wrapper u-col u-gap8">
        <h1 className="heading-24">TOOL</h1>
        <p className="bodytext-13" style={{ color: "var(--soremo-gray)" }}>
          音づくりのための実験的なツール群です。予告なく仕様が変わることがあります。
        </p>
      </div>

      <div className="u-wrapper">
        <CardGrid>
          <ToolCard
            type="button"
            className="u-col"
            onClick={() => navigate("/tool/phrase-bloom")}
          >
            <ToolCardVisual>
              <ToolCardImage src={phraseBloomLogo} alt="PHRASE BLOOM" />
            </ToolCardVisual>
            <ToolCardBody className="u-col u-gap8">
              <ToolTag>SYNTH</ToolTag>
              <span
                className="bodytext-13"
                style={{ color: "var(--soremo-gray)" }}
              >
                音数とスケールを選ぶだけ。流れるような上昇・下降フレーズが、すぐに生まれます。
              </span>
            </ToolCardBody>
          </ToolCard>

          <ToolCard
            type="button"
            className="u-col"
            onClick={() => navigate("/tool/renda-k")}
          >
            <ToolCardVisual>
              <ToolCardImage src={rendaKLogo} alt="RENDA-K" />
            </ToolCardVisual>
            <ToolCardBody className="u-col u-gap8">
              <ToolTag>EFFECTS</ToolTag>
              <span
                className="bodytext-13"
                style={{ color: "var(--soremo-gray)" }}
              >
                読み込んだ音の好きな位置を、BPM
                に乗せてマシンガン連打。加速も曲線も自在なスタッターエフェクトです。
              </span>
            </ToolCardBody>
          </ToolCard>

          <ToolCard
            type="button"
            className="u-col"
            onClick={() => navigate("/tool/step-estimate")}
          >
            <ToolCardVisual>
              <ToolCardImage
                src={stepEstimateLogo}
                alt="ステップ見積もり -超松竹梅-"
              />
            </ToolCardVisual>
            <ToolCardBody className="u-col u-gap8">
              <ToolTag>PDF</ToolTag>
              <span
                className="bodytext-13"
                style={{ color: "var(--soremo-gray)" }}
              >
                項目の選択肢を表に登録するだけ。ステップナビゲーションで見積もりを組み立てて、そのまま御見積書
                PDF に。
              </span>
            </ToolCardBody>
          </ToolCard>

          <ToolCard
            type="button"
            className="u-col"
            onClick={() => navigate("/tool/re-locate")}
          >
            <ToolCardVisual>
              <ToolCardImage src={reLocateLogo} alt="RE:LOCATE" />
            </ToolCardVisual>
            <ToolCardBody className="u-col u-gap8">
              <ToolTag>DAW</ToolTag>
              <span
                className="bodytext-13"
                style={{ color: "var(--soremo-gray)" }}
              >
                Nuendo のプレビュー用 XML を REAPER
                へお引越し。動画・マーカー・サイクルマーカーの音を RPP
                にまとめて移します。
              </span>
            </ToolCardBody>
          </ToolCard>
        </CardGrid>
      </div>
    </div>
  );
};

export default Tools;
